// Phase 1 hooks: tasks, cases, rounds, results, attachments, CLI tokens.
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ApiErrorException, del, get, patch, post, put, uploadToPresignedUrl } from "./client";
import type { ActivityEntry } from "./types/ActivityEntry";
import type { AddFindingRequest } from "./types/AddFindingRequest";
import type { ApiToken } from "./types/ApiToken";
import type { Attachment } from "./types/Attachment";
import type { AttachmentUpload } from "./types/AttachmentUpload";
import type { CaseDetail } from "./types/CaseDetail";
import type { CaseInput } from "./types/CaseInput";
import type { CaseView } from "./types/CaseView";
import type { CreatedToken } from "./types/CreatedToken";
import type { CreateTokenRequest } from "./types/CreateTokenRequest";
import type { Inbox } from "./types/Inbox";
import type { RecordResultRequest } from "./types/RecordResultRequest";
import type { ReplyRequest } from "./types/ReplyRequest";
import type { ReplyView } from "./types/ReplyView";
import type { ResultView } from "./types/ResultView";
import type { RoundDetail } from "./types/RoundDetail";
import type { StartRoundRequest } from "./types/StartRoundRequest";
import type { TaskAction } from "./types/TaskAction";
import type { TaskDetail } from "./types/TaskDetail";
import type { TestingBoard } from "./types/TestingBoard";
import type { UpdateCaseRequest } from "./types/UpdateCaseRequest";

const enc = encodeURIComponent;

export const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024;

/** Same list as the server's `ALLOWED_TYPES`. */
export const ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/json",
  "application/zip",
  "application/x-zip-compressed",
];

/** A task is addressed by its key (`PAY-12`). */
export function taskPath(projectKey: string, key: string) {
  return `/projects/${enc(projectKey)}/tasks/${enc(key)}`;
}

/** Everything cached about one task lives under this key prefix. */
export const taskKey = (projectKey: string, task: string) => ["task", projectKey, task.toUpperCase()] as const;

export function invalidateTask(qc: QueryClient, projectKey: string, task: string) {
  void qc.invalidateQueries({ queryKey: taskKey(projectKey, task) });
  void qc.invalidateQueries({ queryKey: ["tasks", projectKey] });
  void qc.invalidateQueries({ queryKey: ["inbox"] });
  void qc.invalidateQueries({ queryKey: ["testing-board"] });
}

function useTaskInvalidate(projectKey: string, task: string) {
  const qc = useQueryClient();
  return () => invalidateTask(qc, projectKey, task);
}

// ───────────── Inbox & task list ─────────────

export function useInbox() {
  return useQuery({ queryKey: ["inbox"], queryFn: () => get<Inbox>("/inbox") });
}

/** The tester board: one project's, or every project I test in. Refreshes every half minute. */
export function useTestingBoard(projectKey: string | null) {
  return useQuery({
    queryKey: ["testing-board", projectKey ?? ""],
    queryFn: () => get<TestingBoard>(`/testing/board${projectKey ? `?project=${encodeURIComponent(projectKey)}` : ""}`),
    refetchInterval: 30_000,
  });
}

// ───────────── Task ─────────────

export function useTask(projectKey: string, task: string) {
  return useQuery({
    queryKey: [...taskKey(projectKey, task), "detail"],
    queryFn: () => get<TaskDetail>(taskPath(projectKey, task)),
  });
}

export function useTransition(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: (body: { action: TaskAction; note?: string | null }) =>
      post<TaskDetail>(`${taskPath(projectKey, task)}/transition`, { action: body.action, note: body.note ?? null }),
    onSuccess: invalidate,
  });
}


export function useActivity(projectKey: string, task: string, enabled = true) {
  return useQuery({
    queryKey: [...taskKey(projectKey, task), "activity"],
    queryFn: () => get<ActivityEntry[]>(`${taskPath(projectKey, task)}/activity`),
    enabled,
  });
}

// ───────────── Cases ─────────────

export function useCases(projectKey: string, task: string, archived = false) {
  return useQuery({
    queryKey: [...taskKey(projectKey, task), "cases", { archived }],
    queryFn: () => get<CaseView[]>(`${taskPath(projectKey, task)}/cases${archived ? "?archived=true" : ""}`),
  });
}

export function useCaseDetail(projectKey: string, task: string, caseKey: string | null) {
  return useQuery({
    queryKey: [...taskKey(projectKey, task), "case", caseKey],
    queryFn: () => get<CaseDetail>(`${taskPath(projectKey, task)}/cases/${enc(caseKey!)}`),
    enabled: !!caseKey,
  });
}

export function useCreateCase(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: (body: CaseInput) => post<CaseView>(`${taskPath(projectKey, task)}/cases`, body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateCase(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: ({ caseKey, body }: { caseKey: string; body: Partial<UpdateCaseRequest> }) =>
      patch<CaseView>(`${taskPath(projectKey, task)}/cases/${enc(caseKey)}`, body),
    onSuccess: () => invalidate(),
  });
}

export function useReorderCases(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: (keys: string[]) => post<CaseView[]>(`${taskPath(projectKey, task)}/cases/reorder`, { keys }),
    onSuccess: invalidate,
  });
}

export function useReply(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: ({ caseKey, body }: { caseKey: string; body: ReplyRequest }) =>
      post<ReplyView>(`${taskPath(projectKey, task)}/cases/${enc(caseKey)}/replies`, body),
    onSuccess: invalidate,
  });
}

// ───────────── Rounds & results ─────────────

export function useRound(projectKey: string, task: string, number: number | null | undefined) {
  return useQuery({
    queryKey: [...taskKey(projectKey, task), "round", number],
    queryFn: () => get<RoundDetail>(`${taskPath(projectKey, task)}/rounds/${number}`),
    enabled: number != null,
  });
}

export function useStartRound(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: (body: StartRoundRequest) => post<RoundDetail>(`${taskPath(projectKey, task)}/rounds`, body),
    onSuccess: invalidate,
  });
}

export function useFinishRound(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: ({ number, note, untested }: { number: number; note: string; untested: RecordResultRequest | null }) =>
      post<RoundDetail>(`${taskPath(projectKey, task)}/rounds/${number}/finish`, { note: note.trim() || null, untested }),
    onSuccess: invalidate,
  });
}

export function useAddRoundCases(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: ({ number, caseKeys }: { number: number; caseKeys: string[] }) =>
      post<RoundDetail>(`${taskPath(projectKey, task)}/rounds/${number}/cases`, { case_keys: caseKeys }),
    onSuccess: invalidate,
  });
}

/** Records a result; the round view updates immediately and rolls back on error. */
export function useRecordResult(projectKey: string, task: string, number: number) {
  const qc = useQueryClient();
  const roundKey = [...taskKey(projectKey, task), "round", number];
  return useMutation({
    mutationFn: ({ caseKey, body }: { caseKey: string; body: RecordResultRequest }) =>
      put<ResultView>(`${taskPath(projectKey, task)}/rounds/${number}/results/${enc(caseKey)}`, body),
    onMutate: async ({ caseKey, body }) => {
      await qc.cancelQueries({ queryKey: roundKey });
      const previous = qc.getQueryData<RoundDetail>(roundKey);
      if (previous) {
        qc.setQueryData<RoundDetail>(roundKey, {
          ...previous,
          items: previous.items.map((it) =>
            it.case_key === caseKey
              ? { ...it, status: body.status, actual: body.actual, stale: false, revision_tested: it.current_revision }
              : it,
          ),
        });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(roundKey, ctx.previous);
    },
    onSettled: () => invalidateTask(qc, projectKey, task),
  });
}

export function useClearResult(projectKey: string, task: string, number: number) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: (caseKey: string) => del(`${taskPath(projectKey, task)}/rounds/${number}/results/${enc(caseKey)}`),
    onSuccess: invalidate,
  });
}

export function useAddFinding(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: (body: AddFindingRequest) => post<CaseView>(`${taskPath(projectKey, task)}/findings`, body),
    onSuccess: invalidate,
  });
}

// ───────────── Attachments ─────────────

/** Three steps: ask for a presigned URL, PUT the file to S3, mark it complete. */
export function useUploadAttachment(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: async ({ caseKey, roundNumber, file }: { caseKey: string; roundNumber: number | null; file: File }) => {
      if (file.size > MAX_ATTACHMENT_BYTES) throw new ApiErrorException("validation", "", 422);
      const contentType = file.type || "application/octet-stream";
      const target = await post<AttachmentUpload>(`${taskPath(projectKey, task)}/attachments`, {
        case_key: caseKey,
        round_number: roundNumber,
        filename: file.name || "pasted-image.png",
        content_type: contentType,
        size: file.size,
      });
      await uploadToPresignedUrl(target.upload_url, file, contentType);
      return post<Attachment>(`${taskPath(projectKey, task)}/attachments/${enc(target.attachment.id)}/complete`);
    },
    onSuccess: invalidate,
  });
}

export function useDeleteAttachment(projectKey: string, task: string) {
  const invalidate = useTaskInvalidate(projectKey, task);
  return useMutation({
    mutationFn: (id: string) => del(`${taskPath(projectKey, task)}/attachments/${enc(id)}`),
    onSuccess: invalidate,
  });
}

// ───────────── API tokens ─────────────
// Your own (`/me/tokens`), or a bot's, managed by a platform manager (`/users/{id}/tokens`).

const tokensPath = (botId?: string) => (botId ? `/users/${enc(botId)}/tokens` : "/me/tokens");

export function useTokens(enabled = true, botId?: string) {
  return useQuery({ queryKey: ["tokens", botId ?? "me"], queryFn: () => get<ApiToken[]>(tokensPath(botId)), enabled });
}

export function useCreateToken(botId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTokenRequest) => post<CreatedToken>(tokensPath(botId), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tokens", botId ?? "me"] });
      if (botId) void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useRevokeToken(botId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`${tokensPath(botId)}/${enc(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tokens", botId ?? "me"] });
      if (botId) void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
